import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Search, Handshake, User, Phone, Mail, CalendarClock, AlertTriangle, Upload, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import LeadForm from "../components/leads/LeadForm";
import LeadImportDialog from "../components/leads/LeadImportDialog";
import { LEAD_STATUSES, LEAD_SERVICES, LEAD_STATUS_COLORS, LEAD_PRIORITY_COLORS, followupBucket } from "../components/leads/leadUtils";
import { formatMoney } from "../lib/currency";

export default function Leads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [serviceFilter, setServiceFilter] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const canCreate = ["admin", "manager", "ceo", "coo", "sales_head", "sales_member"].includes(user?.role);
  const canImport = ["admin", "manager", "ceo", "coo", "sales_head"].includes(user?.role);

  const load = async () => {
    try {
      const [l, u] = await Promise.all([
        api.get("/leads"),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setLeads(l.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const nameOf = (id) => users.find(u => u.id === id)?.name || "—";

  const filtered = useMemo(() => leads.filter(l => {
    const matchesQ = !q || `${l.lead_name} ${l.company} ${l.contact_person} ${l.lead_id} ${l.email}`.toLowerCase().includes(q.toLowerCase());
    const matchesS = statusFilter === "All" || l.status === statusFilter;
    const matchesP = priorityFilter === "All" || l.priority === priorityFilter;
    const leadServices = Array.isArray(l.service_required) ? l.service_required : (l.service_required ? [l.service_required] : []);
    const matchesSvc = serviceFilter.length === 0 || serviceFilter.some(s => leadServices.includes(s));
    return matchesQ && matchesS && matchesP && matchesSvc;
  }), [leads, q, statusFilter, priorityFilter, serviceFilter]);

  const toggleService = (s) => setServiceFilter(f => f.includes(s) ? f.filter(x => x !== s) : [...f, s]);

  const statuses = ["All", ...LEAD_STATUSES];

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px]">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Pipeline</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Leads</h1>
          <p className="text-neutral-600 text-sm mt-1">{leads.length} opportunit{leads.length === 1 ? "y" : "ies"} in play</p>
        </div>
        {(canCreate || canImport) && (
          <div className="flex gap-2">
            {canImport && (
              <Button onClick={() => setImportOpen(true)} data-testid="import-leads-btn" variant="outline" className="border-[#E4E7EC]">
                <Upload className="w-4 h-4 mr-1" /> Import CSV / Excel
              </Button>
            )}
            {canCreate && (
              <Button onClick={() => setOpen(true)} data-testid="create-lead-btn" className="bg-orange-500 hover:bg-orange-600 text-white">
                <Plus className="w-4 h-4 mr-1" /> Add Lead
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-full sm:w-auto sm:max-w-md sm:flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search leads..." className="pl-9 bg-[#FFFFFF] border-[#E4E7EC]" data-testid="lead-search-input" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {["All", "Low", "Medium", "High"].map(p => (
            <button key={p} onClick={() => setPriorityFilter(p)} data-testid={`priority-filter-${p.toLowerCase()}`} className={`px-3 py-1.5 rounded-lg text-[11px] font-mono uppercase tracking-wider transition-colors border ${priorityFilter === p ? "bg-orange-100 text-orange-700 border-orange-500/30" : "bg-[#FFFFFF] text-neutral-600 border-[#E4E7EC] hover:text-neutral-900"}`}>
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-1 flex-wrap">
        {statuses.map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} data-testid={`status-filter-${s.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`} className={`px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-wider transition-colors border ${statusFilter === s ? "bg-orange-100 text-orange-700 border-orange-500/30" : "bg-[#FFFFFF] text-neutral-600 border-[#E4E7EC] hover:text-neutral-900"}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="service-filter-row">
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mr-1">Service</span>
        {LEAD_SERVICES.map(s => {
          const on = serviceFilter.includes(s);
          return (
            <button
              key={s}
              onClick={() => toggleService(s)}
              data-testid={`service-filter-${s.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
              className={`px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider border transition-colors ${on ? "bg-orange-500 text-white font-semibold border-orange-500" : "bg-[#FFFFFF] text-neutral-600 border-[#E4E7EC] hover:text-neutral-900"}`}>
              {on && <span className="mr-1">✓</span>}{s}
            </button>
          );
        })}
        {serviceFilter.length > 0 && (
          <button onClick={() => setServiceFilter([])} data-testid="service-filter-clear" className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 hover:text-orange-700 flex items-center gap-1 ml-1">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="panel p-10 text-center text-neutral-500">
          <Handshake className="w-8 h-8 mx-auto mb-3 opacity-50" />
          {leads.length === 0 ? (canCreate ? "No leads yet. Click 'Add Lead' to start." : "No leads assigned to you.") : "No leads match your filters."}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(l => {
            const bucket = followupBucket(l.next_followup_date, l.status);
            return (
              <Link key={l.id} to={`/leads/${l.id}`} data-testid={`lead-row-${l.id}`} className="panel panel-hover p-4 flex items-center gap-4 group">
                <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-orange-500/25 to-red-500/15 border border-orange-500/20 grid place-items-center shrink-0 font-bold text-sm">
                  {(l.company || l.lead_name || "?").slice(0,2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono text-neutral-500">{l.lead_id}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${LEAD_STATUS_COLORS[l.status] || ""}`}>{l.status}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${LEAD_PRIORITY_COLORS[l.priority] || ""}`}>{l.priority}</span>
                    {bucket === "overdue" && <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-rose-500/15 border-rose-500/30 text-rose-700 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> Overdue</span>}
                    {bucket === "today" && <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-orange-500/15 border-orange-500/30 text-orange-700 flex items-center gap-1"><CalendarClock className="w-2.5 h-2.5" /> Today</span>}
                  </div>
                  <div className="font-semibold truncate mt-0.5">{l.lead_name} {l.company && <span className="text-neutral-500 text-xs ml-1">· {l.company}</span>}</div>
                  <div className="mt-1 flex items-center gap-3 text-[10px] font-mono text-neutral-500 flex-wrap">
                    {l.contact_person && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {l.contact_person}</span>}
                    {l.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {l.phone}</span>}
                    {l.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {l.email}</span>}
                    <span>Source: <span className="text-neutral-700">{l.lead_source}</span></span>
                    <span>Service: <span className="text-neutral-700">{Array.isArray(l.service_required) ? (l.service_required.join(" · ") || "—") : (l.service_required || "—")}</span></span>
                  </div>
                </div>
                <div className="text-right shrink-0 hidden md:block">
                  {l.lead_value !== undefined && <>
                  <div className="text-[10px] font-mono text-neutral-500">Value</div>
                  <div className="text-sm font-bold text-emerald-700">{formatMoney(l.lead_value, l.currency)}</div>
                  </>}
                  <div className="text-[10px] font-mono text-neutral-500 mt-1">{nameOf(l.assigned_to)}</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <LeadForm open={open} onOpenChange={setOpen} users={users} currentUser={user} onSaved={load} />
      <LeadImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />
    </div>
  );
}
